// Every word a student reads during an English diagnostics activity, in one place, the same
// way bmd.constants.ts holds the Math wording.
//
// Two rules the Math copy already follows and this has to as well:
//
//   Never show the year level. A Year 6 child must not learn they are being asked a Year 4
//   skill, so no screen here names a year.
//
//   Never tell them how they did. A diagnostic measures what a child can do; a running score
//   changes how they answer the rest, and some answers are not marked until their tutor has
//   looked at them. Nothing here congratulates or corrects.

/**
 * Show the skill and its curriculum position above each question.
 *
 * TEMPORARY, and OFF for real students. The curriculum team asked for it so they can see what
 * each question is testing while reviewing coverage on staging. It deliberately exposes the
 * YEAR, which is the one thing the rest of the player is careful never to reveal - a Year 6
 * child must not learn they are being asked a Year 2 skill.
 *
 * Flip to true on staging for the review; set back to false before production. Delete this,
 * the banner in EngDiagAttemptPlayer and `provenance` in studentDelivery.js together when the
 * review is finished.
 */
export const SHOW_SKILL_PROVENANCE = true

export const ENG_DIAG_COPY = Object.freeze({
  PLAYER_TITLE: "English Diagnostics",
  SUBJECT_LABEL: "English",
  SHORT_LABEL: "Diagnostics",
  // A prerequisite test is a different thing from the diagnostic, and a child who has just sat
  // one needs to know this is not the same test again. "Skill check" rather than anything with
  // "prerequisite" in it: the word is for tutors, not for a seven-year-old.
  PREREQ_PLAYER_TITLE: "Skill Check",
  PREREQ_SHORT_LABEL: "Skill check",
  PREREQ_WELCOME_NOTE:
    "A few questions about things that come before what you just did. Answer what you can.",

  // --- Welcome screen -----------------------------------------------------
  WELCOME_TITLE: "Ready when you are",
  RESUME_TITLE: "Pick up where you left off",
  // Plain facts, not reassurance. "Nothing to revise for" is the one thing children ask their
  // tutor about these, so it is said first.
  WELCOME_NOTE: "There's nothing to revise for. Answer what you can, and skip anything you're stuck on.",
  SAVE_NOTE: "If you stop, your answers and your place are saved.",
  START: "Start",
  RESUME: "Carry on",
  TIME_LEFT_PREFIX: "about",
  TIME_LEFT_SUFFIX: "min left",
  ANSWERED_SUFFIX: "answered",

  SECTION_IN_PROGRESS: "In progress",
  SECTION_NEXT: "Next",
  SECTION_DONE: "Done",

  // --- Question screen ----------------------------------------------------
  LOADING: "Loading…",
  SUBMIT_ANSWER: "Submit answer",
  SUBMITTING: "Saving…",
  // Shown on the disabled submit button. Points at the way out rather than just refusing.
  ANSWER_BEFORE_SUBMIT: "Answer the question first, or use Skip for now.",
  SKIP_FOR_NOW: "Skip for now",
  SKIP_SUBTEXT: "Comes back at the end.",
  // Counts what they have done, not where they are in a list: a skipped question comes back, so
  // a position would go backwards. This is answered + 1, which never does.
  QUESTION_LABEL: "Question",
  /** The same thing in the one-line header, where "Question 12" will not fit beside the bar. */
  QUESTION_LABEL_SHORT: "Q",
  // Beside the disabled submit button. Shorter than ANSWER_BEFORE_SUBMIT, which is the button's
  // own tooltip and has room for a whole sentence.
  ANSWER_TO_CONTINUE: "Answer this to carry on",
  /**
   * The same line once a multi-part question is PARTLY done, where "Answer this to carry on"
   * reads as though nothing had been typed at all. Submit now waits for every part, so a child
   * has to be told which ones are still empty rather than left guessing at a grey button.
   *
   * Called with (answered, total) - "2 of 3 parts answered".
   */
  PARTS_ANSWERED: (answered: number, total: number) =>
    `${answered} of ${total} parts answered`,

  // --- Finish screen ------------------------------------------------------
  FINISH_TITLE: "That's everything for now",
  FINISH_NOTE: "If your tutor adds more questions later, they'll show up here.",
  FINISH_STAT_QUESTIONS: "questions answered",

  // --- When something breaks ----------------------------------------------
  ERROR_LOAD: "Unable to load this activity.",
  ERROR_SUBMIT: "Unable to save your answer.",
  ERROR_SKIP: "Unable to move to the next question.",
  /**
   * Shown instead of the raw error when the request never got an answer - a timeout, a dropped
   * connection, the API restarting mid-deploy. "timeout of 30000ms exceeded" is what axios
   * says, and it is not something to put in front of a nine-year-old.
   */
  ERROR_CONNECTION: "We couldn't reach the server. Check your connection and try again.",
})

/**
 * Every word inside a question card: how to work each kind of control, and what to do with the
 * four types that have nothing to answer.
 *
 * Its own object rather than more keys on ENG_DIAG_COPY because this is the set the curriculum
 * team reads and rewrites - it is instructional wording for a child, and they own it. Grouped
 * by the question type it belongs to, using the stored `type` values so a reviewer can line it
 * up against the authoring form.
 *
 * Two rules it follows, both set by the question view:
 *
 *   Tapping comes first in every sentence. Dragging does not work on a touch screen in every
 *   browser, and tapping is what a child tries anyway, so no instruction here asks for a drag
 *   without offering a tap.
 *
 *   Every instruction says how to UNDO. "Students can't change their answers" was the first
 *   thing the curriculum lead reported about these screens.
 */
export const ENG_DIAG_QUESTION_COPY = Object.freeze({
  // --- shared ------------------------------------------------------------
  WORD_BANK: "Word bank",
  TYPE_YOUR_ANSWER: "Type your answer",
  TYPE_HERE: "Type your answer here",
  BLANK_LABEL: "Blank",
  YOUR_ANSWER: "Your answer",

  // --- the worked-example types (Skill Builders only) --------------------
  // A Skill Builder question can carry a solved twin of itself. "Worked example" is the phrase
  // the curriculum team use on the PDFs these replace, and the instruction authored on all four
  // live questions already says "Study the example, then complete the one on the right".
  WORKED_LABEL: "Worked example",
  WORKED_ANSWER: "Answer",
  // Read out in place of the tick on the worked half's correct choice. The tick is a glyph and
  // a colour; neither reaches a screen reader, and the palette comment two files over is right
  // that colour must never be the only signal.
  WORKED_CORRECT: "Correct answer",
  YOUR_TURN: "Your turn",
  WRITE_HERE: "Write your answer here",

  // --- spelling_drop / punctuation_drop / column_match -------------------
  DROP_HINT:
    "Tap a word, then tap where it goes - or drag it there. Tap the cross to empty a space again.",
  DROP_EMPTY: "Drop here",
  DROP_ARMED: "Tap to place here",

  // --- image_sort --------------------------------------------------------
  SORT_HINT:
    "Tap a card, then tap the group it belongs in - or drag it there. Tap a card inside a group to take it back out.",
  SORT_POOL_LABEL: "Cards to sort",
  SORT_POOL_EMPTY: "Every card is sorted. Tap one inside a group to move it.",
  SORT_BIN_EMPTY: "Drop cards here",

  // --- mc_cloze ----------------------------------------------------------
  // "Blank", not "Gap": the `blank` type already numbers its rows "Blank 1", "Blank 2", so a
  // child met both words for the same thing on consecutive questions. The curriculum lead
  // picked this one.
  GAP_LABEL: "Blank",

  // --- punctuation_insert ------------------------------------------------
  MARKS_LABEL: "Punctuation marks",
  INSERT_HINT: "Tap a mark, then tap an insertion point (‸) in the sentence.",

  // --- text_select -------------------------------------------------------
  SELECT_HINT: "Tap in the sentence to choose. Tap again to unselect.",
  SELECT_COUNT_SUFFIX: "selected",

  // --- sentence_rebuild --------------------------------------------------
  REBUILD_HINT:
    "Tap or drag the words into the correct order. Tap a word in your sentence to take it back out.",
  REBUILD_LABEL: "Your sentence",
  REBUILD_EMPTY: "Build your sentence here",
  REBUILD_RESET: "Start again",

  // --- the four types with nothing to answer -----------------------------
  // Each says plainly that there is nothing to type, because an empty submission is marked
  // wrong everywhere else and a child who has learnt that will sit waiting for a box to fill.
  READ_WORDS_TITLE: "Read these out loud",
  READ_WORDS_BODY:
    "Your tutor is listening, so there is nothing to type. Press Submit when you have read them all.",
  PHONEMES_TITLE: "Say each sound out loud",
  PHONEMES_BODY:
    "Break each word into its sounds for your tutor. There is nothing to type - press Submit when you are done.",
  FLASHCARD_TITLE: "Read each card out loud",
  FLASHCARD_BODY:
    "Your tutor will show you these one at a time. Press Submit when you have finished.",
  DISPLAY_TITLE: "Have a look at this",
  DISPLAY_BODY:
    "There is nothing to answer here. Press Submit when you are ready to move on.",
})

/**
 * Every word a student reads during an English SKILL BUILDER.
 *
 * The first rule at the top of this file still holds - no screen here names a year level. THE
 * SECOND ONE DOES NOT, and that is the whole difference between the two activities. A diagnostic
 * stays silent because telling a child they were wrong would change how they answer the rest of
 * it. A Skill Builder is practice for a skill they have already been shown to be weak at, so
 * silence would just be more of the thing that did not work: they are told how each answer went,
 * and the words for that live here.
 *
 * Built on the diagnostics copy, so the shared question types - every word bank, every drop
 * hint, every "nothing to type" explanation - read identically in both. Only what genuinely
 * differs is restated.
 */
export const ENG_SB_COPY = Object.freeze({
  ...ENG_DIAG_COPY,

  PLAYER_TITLE: "Skill Builder",
  SHORT_LABEL: "Practice",

  // --- welcome ------------------------------------------------------------
  WELCOME_TITLE: "Let's practise this",
  RESUME_TITLE: "Pick up where you left off",
  // Says WHY they are here without saying they failed anything.
  WELCOME_NOTE:
    "Your tutor has set some practice on one skill. Take your time - you can see how you did after each question.",
  SKILL_PREFIX: "You are practising",
  START: "Start practising",
  RESUME: "Carry on",

  // --- feedback, which the diagnostic has none of -------------------------
  CORRECT_TITLE: "That's right",
  CORRECT_BODY: "Nice work. Keep going.",
  INCORRECT_TITLE: "Not quite",
  // The correct answer is NOT shown. The authored answer key never leaves the server for a
  // student, and a child told the answer practises reading it rather than the skill.
  INCORRECT_BODY: "That one wasn't right. Have a go at the next one.",
  PENDING_TITLE: "Saved for your tutor",
  PENDING_BODY: "Your tutor will look at this one and mark it themselves.",
  NEXT_QUESTION: "Next question",
  SEE_RESULTS: "Finish",

  // --- finish -------------------------------------------------------------
  FINISH_TITLE: "Practice finished",
  FINISH_NOTE: "Your tutor can see how you got on.",
  FINISH_PENDING_NOTE: "Some answers are waiting for your tutor to mark.",
})
